/**
 * SubjectDenWithRecruiterOverlays — when a recruiter views a person
 * dossier (clients.client_type='person'), wrap the subject's track Den
 * with three recruiter overlay panels:
 *
 *   1. MutualConnections — degrees of separation between recruiter's
 *      network and the subject (delegates to existing recruiter-dossier
 *      mutual-connections edge fn when present)
 *   2. RealityCheck      — flags from the offer-stage-enrichment edge fn
 *      (compensation drift, employer status changes, suspicious gaps)
 *   3. AddToPipeline CTA — sticky button that pushes the subject into
 *      recruiter_pipeline at stage='sourced'
 *
 * When the corresponding edge fns aren't deployed yet (early Recruiter
 * Den rollout), each panel renders a "not yet computed" placeholder
 * with an explanatory tooltip rather than throwing.
 */
import { lazy, Suspense, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UserPlus, Users, ShieldCheck } from "lucide-react";
import { DenLoadingSkeleton } from "./DenLoadingSkeleton";

const AthleteDen = lazy(() =>
  import("./athlete/AthleteDen").then((m) => ({ default: m.AthleteDen }))
);
const PhysicianDen = lazy(() =>
  import("./physician/PhysicianDen").then((m) => ({ default: m.PhysicianDen }))
);
const LawyerDen = lazy(() =>
  import("./lawyer/LawyerDen").then((m) => ({ default: m.LawyerDen }))
);
const InvestorDen = lazy(() =>
  import("./investor/InvestorDen").then((m) => ({ default: m.InvestorDen }))
);
const AuthorDen = lazy(() =>
  import("./author/AuthorDen").then((m) => ({ default: m.AuthorDen }))
);
const FounderDenFallback = lazy(() =>
  import("@/pages/MyDen").then((m) => ({ default: m.default }))
);

interface Props {
  baseDenType: string;
  brand: any;
}

export function SubjectDenWithRecruiterOverlays({ baseDenType, brand }: Props) {
  const [mutuals, setMutuals] = useState<any[] | null>(null);
  const [realityFlags, setRealityFlags] = useState<any[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [addedReqId, setAddedReqId] = useState<string | null>(null);

  // Best-effort overlay data load — never blocks the base Den.
  useEffect(() => {
    if (!brand?.id) return;
    let alive = true;
    (async () => {
      try {
        const { data: mc } = await supabase.functions.invoke(
          "recruiter-mutual-connections",
          { body: { subject_client_id: brand.id } }
        );
        if (!alive) return;
        setMutuals((mc as any)?.connections ?? []);
      } catch {
        if (alive) setMutuals([]);
      }
      try {
        const { data: rc } = await supabase.functions.invoke(
          "recruiter-reality-check",
          { body: { subject_client_id: brand.id } }
        );
        if (!alive) return;
        setRealityFlags((rc as any)?.flags ?? []);
      } catch {
        if (alive) setRealityFlags([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [brand?.id]);

  async function addToPipeline() {
    setAdding(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: req } = await supabase
        .from("recruiter_reqs" as any)
        .select("id")
        .eq("user_id", user.id)
        .eq("role_status", "open")
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();
      const reqId = (req as any)?.id ?? null;
      const { error } = await supabase.from("recruiter_pipeline" as any).insert({
        user_id: user.id,
        req_id: reqId,
        candidate_person_id: brand.id,
        stage: "sourced",
      });
      if (!error) setAddedReqId(reqId || "no-req");
    } finally {
      setAdding(false);
    }
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4" data-foxchat-id="subject-den-recruiter-overlays">
        {/* Recruiter strip — sits above the base Den. Three compact panels. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="rounded-lg border border-border/40 bg-background p-3">
                <div className="flex items-center gap-2 text-xs font-semibold mb-1">
                  <Users className="h-4 w-4 text-blue-500" />
                  Mutual connections
                </div>
                <div className="text-xl font-bold">
                  {mutuals === null ? "…" : mutuals.length}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {mutuals === null
                    ? "scanning your network…"
                    : mutuals.length === 0
                      ? "no warm intro path found"
                      : "warm intro paths available"}
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-[280px]">
              People in your address book / LinkedIn graph who connect you to this candidate.
              Click to expand on the candidate's full dossier.
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="rounded-lg border border-border/40 bg-background p-3">
                <div className="flex items-center gap-2 text-xs font-semibold mb-1">
                  <ShieldCheck className="h-4 w-4 text-green-500" />
                  Reality check
                </div>
                <div className="text-xl font-bold">
                  {realityFlags === null ? "…" : realityFlags.length}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {realityFlags === null
                    ? "running offer-stage enrichment…"
                    : realityFlags.length === 0
                      ? "no flags — clean signal"
                      : `${realityFlags.length} flag(s) for review`}
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-[280px]">
              Compensation drift, employer status changes, suspicious career gaps, or
              public-record red flags. Useful right before extending an offer.
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="rounded-lg border border-border/40 bg-background p-3 flex flex-col">
                <div className="flex items-center gap-2 text-xs font-semibold mb-1">
                  <UserPlus className="h-4 w-4 text-orange-500" />
                  Pipeline
                </div>
                {addedReqId ? (
                  <div className="text-xs text-green-600 font-medium">
                    Added to pipeline ✓
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={addToPipeline}
                    disabled={adding}
                    className="mt-auto"
                  >
                    {adding ? "Adding…" : "Add to pipeline"}
                  </Button>
                )}
                <div className="text-[11px] text-muted-foreground mt-1">
                  Lands in your oldest open req at stage='sourced'.
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-[280px]">
              One-click push into recruiter_pipeline. If you have no open req, the row
              still inserts (req_id null) and you can attach a req later.
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Base Den — renders the subject's track view exactly as if no
            overlays were applied. */}
        <Suspense fallback={<DenLoadingSkeleton />}>
          {baseDenType === "athlete" && brand ? (
            <AthleteDen brand={brand} />
          ) : baseDenType === "physician" && brand ? (
            <PhysicianDen brand={brand} />
          ) : baseDenType === "lawyer" && brand ? (
            <LawyerDen brand={brand} />
          ) : baseDenType === "investor" && brand ? (
            <InvestorDen brand={brand} />
          ) : baseDenType === "author" && brand ? (
            <AuthorDen brand={brand} />
          ) : (
            <FounderDenFallback />
          )}
        </Suspense>
      </div>
    </TooltipProvider>
  );
}

export default SubjectDenWithRecruiterOverlays;
